import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, Form, Card, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faSave } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import Swal from "sweetalert2";
import useFetch from "../../../../hooks/useFetch";

export default function InventarioIconografia() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { get, post, put } = useFetch();

    const [state, setState] = useState({
        acao: location.state?.acao || (id === "criar" ? "Criar" : "Visualizar"),
        dados: { id_usuario: null, data_inventario: "", observacoes: "" },
        fotografiasSelecionadas: [],
        loading: id !== "criar",
    });

    const [opcoes, setOpcoes] = useState({ usuarios: [], fotografias: [] });

    const carregarOpcoes = useCallback(async () => {
        const [usuariosRes, fotografiasRes] = await Promise.all([
            get("/usuario?page=1"),
            get("/fotografia?page=1"),
        ]);
        setOpcoes({
            usuarios: usuariosRes.ok
                ? (usuariosRes.result.data ?? []).map((u) => ({ value: u.id_usuario, label: u.nome_pessoa || u.nome_usuario }))
                : [],
            fotografias: fotografiasRes.ok
                ? (fotografiasRes.result.data ?? []).map((f) => ({ value: f.id_fotografia, label: `${f.numero_tombo} - ${f.titulo_atribuido}` }))
                : [],
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        carregarOpcoes();

        if (id === "criar") {
            setState((prev) => ({ ...prev, loading: false }));
            return;
        }

        async function carregarInventario() {
            let response = await get(`/inventariofotografia/${id}`);
            if (response.status === 200 && response.result.data) {
                const inv = response.result.data;
                setState((prev) => ({
                    ...prev,
                    dados: {
                        id_usuario: inv.id_usuario,
                        data_inventario: inv.data_inventario ?? "",
                        observacoes: inv.observacoes ?? "",
                    },
                    fotografiasSelecionadas: (inv.Fotografia ?? []).map((f) => ({
                        value: f.id_fotografia,
                        label: `${f.numero_tombo} - ${f.titulo_atribuido}`,
                    })),
                    loading: false,
                }));
            } else {
                setState((prev) => ({ ...prev, loading: false }));
                Swal.fire({ title: "Inventário não encontrado", icon: "error", confirmButtonColor: "#587b7f" });
            }
        }
        carregarInventario();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    function alterarCampo(event) {
        const { name, value } = event.target;
        setState((prev) => ({ ...prev, dados: { ...prev.dados, [name]: value } }));
    }

    async function salvar() {
        if (!state.dados.id_usuario) {
            Swal.fire({ title: "Selecione o usuário responsável", icon: "warning", confirmButtonColor: "#587b7f" });
            return;
        }

        try {
            let idInventario = id;
            let response;
            if (state.acao === "Criar") {
                response = await post("/inventariofotografia", state.dados);
            } else {
                response = await put(`/inventariofotografia/${id}`, state.dados);
            }

            if (!(response.status === 200 || response.status === 201)) {
                Swal.fire({
                    title: "Não foi possível salvar o inventário",
                    text: response.result?.message ?? "",
                    icon: "error",
                    confirmButtonColor: "#587b7f",
                });
                return;
            }

            if (state.acao === "Criar") {
                idInventario = response.result.data.id_inventario;
            }

            await put(`/inventariofotografia/${idInventario}/fotografias`, {
                id_fotografia: state.fotografiasSelecionadas.map((f) => f.value),
            });

            Swal.fire({ title: "Inventário salvo!", icon: "success", confirmButtonColor: "#587b7f" });
            navigate("/admin/iconografico/inventarios");
        } catch (error) {
            console.error("Erro no salvar():", error);
            Swal.fire({ title: "Erro de conexão", text: "Não foi possível falar com o servidor.", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    const selectStyles = {
        control: (base, stateProps) => ({
            ...base,
            borderRadius: '10px',
            borderColor: '#e2e8f0',
            minHeight: '44px',
            backgroundColor: stateProps.isDisabled ? '#e9ecef' : base.backgroundColor,
        }),
        menu: (base) => ({ ...base, zIndex: 5 }),
        option: (base, stateProps) => ({
            ...base,
            backgroundColor: stateProps.isSelected ? '#587b7f' : stateProps.isFocused ? '#e8f0f1' : '#fff',
            color: stateProps.isSelected ? '#fff' : '#2c3e50',
        }),
        singleValue: (base) => ({ ...base, color: '#2c3e50' }),
        multiValue: (base) => ({ ...base, backgroundColor: '#e8f0f1' }),
        multiValueLabel: (base) => ({ ...base, color: '#2c3e50' }),
        multiValueRemove: (base) => ({ ...base, color: '#587b7f', ':hover': { backgroundColor: '#587b7f', color: '#fff' } }),
        placeholder: (base) => ({ ...base, color: '#6c757d' }),
    };

    const encontrarOpcao = (lista, valor) => lista.find((o) => o.value === valor) ?? null;

    const somenteLeitura = state.acao === "Visualizar";

    if (state.loading) {
        return (
            <div className="admin-page-container text-center py-5">
                <div className="spinner-border text-teal"></div>
            </div>
        );
    }

    return (
        <div className="admin-page-container">
            <div className="mb-4">
                <Link to="/admin/iconografico/inventarios" className="btn-back-modern">
                    <FontAwesomeIcon icon={faAngleLeft} className="me-2" /> Voltar aos Inventários
                </Link>
            </div>

            <Card className="filter-card-admin">
                <Card.Body className="p-4 p-md-5">
                    <div className="section-title-modern mb-5">
                        <div className="title-accent"></div>
                        <div>
                            <h2 className="admin-title">{state.acao} Inventário (Foto)</h2>
                            <p className="admin-subtitle small mb-0">Registro de conferência técnica de acervo iconográfico</p>
                        </div>
                    </div>

                    <Form>
                        <Row className="g-4 mb-4">
                            <Col md={6}>
                                <Form.Label className="label-admin">* Usuário Responsável</Form.Label>
                                <Select
                                    options={opcoes.usuarios}
                                    styles={selectStyles}
                                    placeholder="Selecione o usuário..."
                                    isDisabled={somenteLeitura}
                                    value={encontrarOpcao(opcoes.usuarios, state.dados.id_usuario)}
                                    onChange={(op) => setState((prev) => ({ ...prev, dados: { ...prev.dados, id_usuario: op?.value ?? null } }))}
                                />
                            </Col>

                            <Col md={3}>
                                <Form.Label className="label-admin">Data da Realização</Form.Label>
                                <Form.Control type="date" className="input-moderno" name="data_inventario" value={state.dados.data_inventario ?? ""} onChange={alterarCampo} disabled={somenteLeitura} />
                            </Col>

                            <Col md={12}>
                                <Form.Label className="label-admin">Fotografias Inventariadas (Múltipla Seleção)</Form.Label>
                                <Select
                                    isMulti
                                    options={opcoes.fotografias}
                                    styles={selectStyles}
                                    placeholder="Pesquise por número de tombo ou título..."
                                    isDisabled={somenteLeitura}
                                    value={state.fotografiasSelecionadas}
                                    onChange={(ops) => setState((prev) => ({ ...prev, fotografiasSelecionadas: ops ?? [] }))}
                                />
                            </Col>

                            <Col md={12}>
                                <Form.Label className="label-admin">Observações</Form.Label>
                                <Form.Control
                                    as="textarea" rows={3}
                                    className="input-moderno"
                                    name="observacoes"
                                    placeholder="Notas adicionais sobre o estado físico ou localização das fotografias..."
                                    value={state.dados.observacoes ?? ""}
                                    onChange={alterarCampo}
                                    disabled={somenteLeitura}
                                />
                            </Col>
                        </Row>

                        {!somenteLeitura && (
                            <div className="d-flex justify-content-end mt-5 pt-3 border-top">
                                <Button className="btn-apply-filters px-5" onClick={salvar}>
                                    <FontAwesomeIcon icon={faSave} className="me-2" /> Salvar Inventário
                                </Button>
                            </div>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
}
